import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import AuthenticatedMedia from "../components/AuthenticatedMedia";
import CompactPostEditor, { editorHtmlToText, sanitizeEditorHtml } from "../components/CompactPostEditor";
import { HashtagInput } from "../components/SocialTextInputs";
import { Can, useAccess } from "../components/AccessContext";
import { showError, showSuccess, showWarning } from "../components/ToastNotifications";
import { SkeletonForm, SkeletonRegion } from "../components/skeleton/Skeleton";
import PostPreview from "../components/postStudio/PostPreview";
import StudioDialog from "../components/postStudio/StudioDialog";
import StudioField from "../components/postStudio/StudioField";
import { updatePostWithStatus } from "../components/postStudio/postStudioApi";
import supportedContentTypes from "../config/contentTypes";
import {
  captionLimits, hashtagsToList, hashtagsToText, isLocked, monthKey, platformName, studioPlatforms, studioRoutes, toDateTimeInput
} from "../components/postStudio/postStudioUtils";
import { MediaPicker } from "./MediaLibrary";
import "../components/postStudio/PostStudio.css";

const maxUploadBytes = 12 * 1024 * 1024;
const maxMediaItems = 10;
const routes = studioRoutes.MANUAL;
const blankDraft = () => ({ title: "", platform: studioPlatforms[0], description: "", hashtags: "", cta: "", contentType: "Informational", media: [], status: "DRAFT", scheduledAt: "" });

const readDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("Unable to read the selected file."));
  reader.readAsDataURL(file);
});

const draftFromPost = (post) => ({
  title: post.title || "",
  platform: post.platform || studioPlatforms[0],
  // Older manual posts may only have a plain caption; show it in the rich editor.
  description: sanitizeEditorHtml(post.description || (post.caption ? `<p>${post.caption.replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[char])}</p>` : "")),
  hashtags: hashtagsToText(post.hashtags),
  cta: post.cta || "",
  contentType: post.contentType || "Informational",
  media: post.media || [],
  status: ["DRAFT", "APPROVED", "SCHEDULED"].includes(post.status) ? post.status : "DRAFT",
  scheduledAt: toDateTimeInput(post.scheduledAt)
});

export const validateManualDraft = (draft) => {
  const errors = {};
  const caption = editorHtmlToText(draft.description);
  if (!draft.title.trim()) errors.title = "Add a post title.";
  else if (draft.title.trim().length > 255) errors.title = "Keep the title under 255 characters.";
  if (!studioPlatforms.includes(draft.platform)) errors.platform = "Choose a social platform.";
  if (!caption) errors.description = "Write a caption for this post.";
  else if (caption.length > (captionLimits[draft.platform] || 6000)) errors.description = `${platformName(draft.platform)} captions can be up to ${captionLimits[draft.platform]} characters.`;
  if (hashtagsToList(draft.hashtags).length > 30) errors.hashtags = "Use 30 hashtags or fewer.";
  if (draft.cta.length > 500) errors.cta = "Keep the call to action under 500 characters.";
  if (draft.status === "SCHEDULED") {
    const when = new Date(draft.scheduledAt);
    if (!draft.scheduledAt || Number.isNaN(when.getTime())) errors.scheduledAt = "Choose a date and time to schedule.";
    else if (when <= new Date()) errors.scheduledAt = "Choose a time in the future.";
  }
  return errors;
};

const ManualPostEditor = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { can } = useAccess();
  const isEditing = Boolean(postId);
  const fileInput = useRef(null);
  const [draft, setDraft] = useState(blankDraft);
  const [original, setOriginal] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [loadError, setLoadError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  useEffect(() => {
    let active = true;
    apiRequest("/api/social/accounts")
      .then((result) => { if (active) setAccounts((result.accounts || []).filter((account) => account.status === "CONNECTED")); })
      .catch(() => { if (active) setAccounts([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!isEditing) return undefined;
    let active = true;
    setLoading(true);
    apiRequest(`/api/posts/${encodeURIComponent(postId)}`)
      .then((result) => {
        if (!active) return;
        setOriginal(result.post);
        setDraft(draftFromPost(result.post));
      })
      .catch((error) => { if (active) setLoadError(error.message || "Unable to load this post."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isEditing, postId]);

  const update = (changes) => {
    setDraft((current) => ({ ...current, ...changes }));
    setDirty(true);
    setErrors((current) => Object.fromEntries(Object.entries(current).filter(([field]) => !(field in changes))));
  };

  const account = accounts.find((item) => item.platform === draft.platform);
  const caption = editorHtmlToText(draft.description);
  const limit = captionLimits[draft.platform];
  const previewPost = useMemo(() => ({
    ...draft, caption, hashtags: hashtagsToList(draft.hashtags),
    scheduledAt: draft.status === "SCHEDULED" && draft.scheduledAt ? new Date(draft.scheduledAt).toISOString() : null
  }), [caption, draft]);

  const uploadFiles = async (files) => {
    const selected = [...files];
    if (!selected.length) return;
    if (draft.media.length + selected.length > maxMediaItems) {
      showWarning(`A post can include up to ${maxMediaItems} media items.`);
      return;
    }
    const invalid = selected.find((file) => !/^(image|video)\//.test(file.type) || file.size > maxUploadBytes);
    if (invalid) {
      showWarning(`${invalid.name} must be an image or video under 12 MB.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of selected) {
        const type = file.type.startsWith("video/") ? "video" : "image";
        const result = await apiRequest("/api/posts/media", { method: "POST", body: JSON.stringify({ dataUrl: await readDataUrl(file), name: file.name, type }) });
        uploaded.push({ id: result.mediaId, name: file.name, mimeType: result.mimeType || file.type, url: result.mediaUrl });
      }
      update({ media: [...draft.media, ...uploaded] });
      showSuccess(uploaded.length === 1 ? "Media uploaded." : `${uploaded.length} files uploaded.`);
    } catch (error) {
      showError(error.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const save = async ({ publish = false } = {}) => {
    const nextErrors = validateManualDraft(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      showWarning("Fix the highlighted fields before saving.");
      return;
    }
    const scheduledAt = draft.status === "SCHEDULED" ? new Date(draft.scheduledAt).toISOString() : null;
    const body = {
      title: draft.title.trim(),
      description: sanitizeEditorHtml(draft.description),
      caption,
      hashtags: hashtagsToList(draft.hashtags),
      cta: draft.cta.trim(),
      platform: draft.platform,
      contentType: draft.contentType,
      mediaIds: draft.media.map((item) => item.id)
    };
    setSaving(true);
    try {
      let saved;
      if (isEditing) {
        saved = await updatePostWithStatus(original, body, draft.status, scheduledAt);
      } else {
        saved = (await apiRequest("/api/posts", {
          method: "POST",
          body: JSON.stringify({ ...body, status: draft.status, scheduledAt, month: monthKey(scheduledAt ? new Date(scheduledAt) : new Date()) })
        })).post;
      }
      setDirty(false);
      if (publish) {
        await apiRequest(`/api/posts/${saved.id}/publish`, { method: "POST", body: JSON.stringify({}) });
        showSuccess("Post published.");
      } else {
        showSuccess(draft.status === "SCHEDULED" ? "Post scheduled." : isEditing ? "Post updated." : "Post saved.");
      }
      navigate(routes.list);
    } catch (error) {
      showError(error.message || "The post could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const leave = () => (dirty ? setConfirmLeave(true) : navigate(routes.list));

  if (loading) return <section className="studio-page"><SkeletonRegion label="Loading post"><SkeletonForm fields={6} columns={2} textarea /></SkeletonRegion></section>;
  if (loadError) return <section className="studio-page"><div className="studio-empty" role="alert"><span className="studio-empty-icon is-error"><i className="ti ti-alert-triangle" aria-hidden="true" /></span><h3>Post unavailable</h3><p>{loadError}</p><Link className="btn btn-outline-primary" to={routes.list}>Back to Manual Post Studio</Link></div></section>;
  if (original?.generationSource === "AI") return <section className="studio-page"><div className="studio-empty"><span className="studio-empty-icon"><i className="ti ti-sparkles" aria-hidden="true" /></span><h3>This is an AI-generated post</h3><p>AI posts are edited in AI Post Studio, where you can also regenerate individual fields.</p><Link className="btn btn-primary" to={studioRoutes.AI.edit(original.id)}>Open in AI Post Studio</Link></div></section>;
  if (isLocked(original)) return <section className="studio-page"><div className="studio-empty"><span className="studio-empty-icon"><i className="ti ti-lock" aria-hidden="true" /></span><h3>This post has been published</h3><p>Published posts can't be edited. Duplicate it from the listing to reuse its content.</p><Link className="btn btn-outline-primary" to={routes.list}>Back to Manual Post Studio</Link></div></section>;

  const canSchedule = can("user.posts.schedule", "user.content_calendar.schedule");
  const canPublish = can("user.posts.publish") && Boolean(account?.publishingSupported);

  return <section className="studio-page">
    <header className="studio-header">
      <div>
        <Link className="studio-back" to={routes.list} onClick={(event) => { if (dirty) { event.preventDefault(); setConfirmLeave(true); } }}><i className="ti ti-arrow-left" aria-hidden="true" /> Manual Post Studio</Link>
        <h1>{isEditing ? "Edit post" : "Create post"}</h1>
        <p>Write your own copy, add media and choose when it goes live.</p>
      </div>
    </header>

    <form className="studio-editor" noValidate onSubmit={(event) => { event.preventDefault(); save(); }}>
      <div className="studio-editor-main">
        <section className="studio-panel">
          <h2 className="studio-panel-title">Content</h2>
          <div className="studio-form-grid">
            <StudioField label="Post title" wide error={errors.title}>
              <input className="form-control" maxLength={255} value={draft.title} onChange={(event) => update({ title: event.target.value })} placeholder="Internal title for this post" />
            </StudioField>
            <StudioField label="Social platform & account" error={errors.platform}
              hint={account ? `Publishes to ${account.accountName || account.username || platformName(draft.platform)}.` : <>No connected account. <Link to="/social-accounts">Connect {platformName(draft.platform)}</Link> to publish.</>}>
              <select className="form-select" value={draft.platform} onChange={(event) => update({ platform: event.target.value })}>
                {studioPlatforms.map((item) => {
                  const connected = accounts.find((entry) => entry.platform === item);
                  return <option key={item} value={item}>{platformName(item)}{connected ? ` · ${connected.accountName || connected.username || "Connected"}` : " · Not connected"}</option>;
                })}
              </select>
            </StudioField>
            <StudioField label="Content type">
              <select className="form-select" value={draft.contentType} onChange={(event) => update({ contentType: event.target.value })}>{supportedContentTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select>
            </StudioField>
            <div className={`studio-field is-wide${errors.description ? " has-error" : ""}`} role="group" aria-labelledby="manual-caption-label">
              <span className="studio-field-label"><span id="manual-caption-label">Caption / description</span></span>
              <CompactPostEditor value={draft.description} maxLength={6000} onChange={(description) => update({ description })} />
              <small className={`studio-field-hint${limit && caption.length > limit ? " is-error" : ""}`}>{caption.length}/{limit || 6000} characters for {platformName(draft.platform)} · Use the toolbar for bold, lists, links and emojis.</small>
              {errors.description && <small className="studio-field-error" role="alert">{errors.description}</small>}
            </div>
            <StudioField label="Hashtags" error={errors.hashtags}>
              <HashtagInput id="manual-hashtags" value={draft.hashtags} onChange={(hashtags) => update({ hashtags })} />
            </StudioField>
            <StudioField label="Call to action (optional)" error={errors.cta}>
              <input className="form-control" maxLength={500} value={draft.cta} onChange={(event) => update({ cta: event.target.value })} placeholder="e.g. Book a visit at example.com" />
            </StudioField>
          </div>
        </section>

        <section className="studio-panel">
          <div className="studio-panel-head"><h2 className="studio-panel-title">Media</h2><small>{draft.media.length}/{maxMediaItems} items · images or videos up to 12 MB</small></div>
          <div className="studio-media-actions">
            <Can permission="user.posts.create"><button type="button" className="btn btn-outline-primary" disabled={uploading || draft.media.length >= maxMediaItems} onClick={() => fileInput.current?.click()}><i className="ti ti-upload" aria-hidden="true" /> {uploading ? "Uploading…" : "Upload image or video"}</button></Can>
            <input ref={fileInput} type="file" className="visually-hidden" accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime" multiple aria-label="Upload image or video" onChange={(event) => uploadFiles(event.target.files)} />
            <Can permission="user.media_library.view"><button type="button" className="btn btn-light" onClick={() => setPickerOpen(true)}><i className="ti ti-photo" aria-hidden="true" /> Choose from Media Library</button></Can>
          </div>
          {draft.media.length > 0 ? <ul className="studio-media-list">{draft.media.map((item, index) => <li key={item.id}>
            {item.mimeType?.startsWith("video/") ? <AuthenticatedMedia src={item.url} video muted /> : <AuthenticatedMedia src={item.url} alt="" />}
            <span>{item.name || `Media ${index + 1}`}</span>
            <button type="button" className="studio-icon-button" aria-label={`Remove ${item.name || `media ${index + 1}`}`} onClick={() => update({ media: draft.media.filter((entry) => entry.id !== item.id) })}><i className="ti ti-x" aria-hidden="true" /></button>
          </li>)}</ul> : <p className="studio-muted">No media attached. Text-only posts are supported on most platforms.</p>}
        </section>

        <section className="studio-panel">
          <h2 className="studio-panel-title">Status &amp; schedule</h2>
          <div className="studio-segmented" role="radiogroup" aria-label="Post status">
            {[["DRAFT", "Draft", "ti-file-pencil"], ["APPROVED", "Ready", "ti-circle-check"], ["SCHEDULED", "Schedule", "ti-calendar-time"]].map(([value, label, icon]) => (
              <label key={value} className={draft.status === value ? "is-active" : ""}>
                <input type="radio" name="manual-status" value={value} checked={draft.status === value} disabled={value === "SCHEDULED" && !canSchedule} onChange={() => update({ status: value })} />
                <i className={`ti ${icon}`} aria-hidden="true" /> {label}
              </label>
            ))}
          </div>
          {draft.status === "SCHEDULED" && <StudioField label="Publish date and time" error={errors.scheduledAt}
            hintTone="warning" hint={!account && `Scheduled posts publish to a connected ${platformName(draft.platform)} account. Connect one before the scheduled time.`}>
            <input className="form-control" type="datetime-local" min={toDateTimeInput(new Date())} value={draft.scheduledAt} onChange={(event) => update({ scheduledAt: event.target.value })} />
          </StudioField>}
        </section>
      </div>

      <aside className="studio-editor-side">
        <section className="studio-panel is-sticky">
          <div className="studio-panel-head"><h2 className="studio-panel-title">Preview</h2><small>{platformName(draft.platform)}</small></div>
          <PostPreview post={previewPost} accountName={account?.accountName || account?.username} richText />
        </section>
      </aside>

      <footer className="studio-editor-footer">
        <button type="button" className="btn btn-light" disabled={saving} onClick={leave}>Cancel</button>
        {canPublish && <button type="button" className="btn btn-outline-primary" disabled={saving || uploading} onClick={() => save({ publish: true })}><i className="ti ti-send" aria-hidden="true" /> Save &amp; publish now</button>}
        <button type="submit" className="btn btn-primary studio-primary-button" disabled={saving || uploading}>{saving ? "Saving…" : draft.status === "SCHEDULED" ? "Save & schedule" : isEditing ? "Save changes" : draft.status === "APPROVED" ? "Save as ready" : "Save draft"}</button>
      </footer>
    </form>

    {pickerOpen && <MediaPicker initialSelected={draft.media} onClose={() => setPickerOpen(false)} onConfirm={(media) => {
      if (media.length > maxMediaItems) { showWarning(`A post can include up to ${maxMediaItems} media items.`); return; }
      update({ media });
      setPickerOpen(false);
    }} />}
    {confirmLeave && <StudioDialog role="alertdialog" eyebrow="UNSAVED CHANGES" title="Discard your changes?" onClose={() => setConfirmLeave(false)}
      actions={<><button type="button" className="btn btn-light" onClick={() => setConfirmLeave(false)}>Keep editing</button><button type="button" className="btn btn-danger" onClick={() => navigate(routes.list)}>Discard</button></>}>
      <p className="studio-dialog-copy">You have edits that haven't been saved.</p>
    </StudioDialog>}
  </section>;
};

export default ManualPostEditor;
